import { useRef, useCallback, useState, useEffect } from 'react';

/**
 * Custom hook for detecting swipe gestures with smooth visual feedback
 * Fixed to prevent accidental closes while scrolling content
 */
export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
  edgeSize = 30,
  edgeOnly = false,
  enableVisualFeedback = false,
  verticalOnly = false,
} = {}) {
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchStartedFromEdge = useRef(false);
  const directionLocked = useRef(null);
  const currentY = useRef(0);
  const animationFrameId = useRef(null);
  const scrollableElement = useRef(null);
  const wasAtTop = useRef(false);
  
  const [swipeOffset, setSwipeOffset] = useState({ x: 0, y: 0 });
  const [isSwiping, setIsSwiping] = useState(false);

  useEffect(() => {
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, []);

  const updateOffset = useCallback(() => {
    setSwipeOffset({ x: 0, y: currentY.current });
    animationFrameId.current = null;
  }, []);

  const scheduleUpdate = useCallback(() => {
    if (!animationFrameId.current) {
      animationFrameId.current = requestAnimationFrame(updateOffset);
    }
  }, [updateOffset]);

  // Find scrollable parent
  const findScrollableParent = (element) => {
    if (!element) return null;
    let current = element;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  };

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchStartedFromEdge.current = touch.clientX <= edgeSize;
    directionLocked.current = null;
    
    // Find scrollable element and check if at top
    scrollableElement.current = findScrollableParent(e.target);
    wasAtTop.current = !scrollableElement.current || scrollableElement.current.scrollTop <= 5;
    
    if (enableVisualFeedback) {
      setIsSwiping(true);
      currentY.current = 0;
      setSwipeOffset({ x: 0, y: 0 });
    }
  }, [edgeSize, enableVisualFeedback]);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    
    // Determine direction on first significant move
    if (!directionLocked.current && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      directionLocked.current = Math.abs(deltaY) > Math.abs(deltaX) ? 'vertical' : 'horizontal';
    }
    
    // For vertical-only mode (modals)
    if (verticalOnly) {
      if (directionLocked.current !== 'vertical') return;
      if (deltaY <= 0) return; // Only downward swipes
      if (!wasAtTop.current) return; // Must have started at top
      
      if (enableVisualFeedback) {
        const resistance = 0.35;
        currentY.current = deltaY * resistance;
        scheduleUpdate();
      }
    }
    // For horizontal swipes - direction lock is set, that's all we need
    // The actual navigation happens in handleTouchEnd
  }, [verticalOnly, enableVisualFeedback, scheduleUpdate]);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null || touchStartY.current === null) {
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;

    // Fallback: determine direction if not already locked
    if (!directionLocked.current && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      directionLocked.current = Math.abs(deltaY) > Math.abs(deltaX) ? 'vertical' : 'horizontal';
    }

    const isHorizontal = directionLocked.current === 'horizontal' || 
      (!directionLocked.current && Math.abs(deltaX) > Math.abs(deltaY));
    const isVertical = directionLocked.current === 'vertical' ||
      (!directionLocked.current && Math.abs(deltaY) > Math.abs(deltaX));

    // For vertical swipes on modals
    if (verticalOnly && isVertical) {
      // Only close if swiped down significantly AND started at top
      if (deltaY > threshold && wasAtTop.current) {
        onSwipeDown?.();
      }
    }
    // For horizontal swipes (page navigation)
    else if (!verticalOnly && isHorizontal) {
      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0 && (!edgeOnly || touchStartedFromEdge.current)) {
          onSwipeRight?.();
        } else if (deltaX < 0) {
          onSwipeLeft?.();
        }
      }
    }

    // Reset everything
    touchStartX.current = null;
    touchStartY.current = null;
    touchStartedFromEdge.current = false;
    directionLocked.current = null;
    scrollableElement.current = null;
    wasAtTop.current = false;
    
    if (enableVisualFeedback) {
      setIsSwiping(false);
      currentY.current = 0;
      setSwipeOffset({ x: 0, y: 0 });
    }
    
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
  }, [onSwipeLeft, onSwipeRight, onSwipeDown, threshold, edgeOnly, verticalOnly, enableVisualFeedback]);

  const handlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  // Style - only vertical movement, no horizontal wiggle
  const swipeStyle = enableVisualFeedback ? {
    transform: `translate3d(0, ${swipeOffset.y}px, 0)`,
    transition: isSwiping ? 'none' : 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
    willChange: isSwiping ? 'transform' : 'auto',
  } : {};

  return {
    ...handlers,
    swipeStyle,
    isSwiping,
    swipeOffset,
  };
}

export default useSwipeGesture;